import { BCMSRecord, Branding, useBCMSContext, useModel, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import Classes, { Account, Transaction, TransferTemplate, TransferTransaction } from "model";
import React from "react";

import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { UIButton, UICheckbox } from "components/ui";
import { getDefaultTransfer } from "components/workflows/MakeTransferWorkflow";
import MakeTransfer from "components/workflows/MakeTransferWorkflow/classes/MakeTransfer";
import MakeTransferUI from "components/workflows/MakeTransferWorkflow/classes/MakeTransferUI";

import CustomContentHeaderWidget from "components/shared/views/CustomContentHeaderWidget";
import "./styles.css";
import "./mobile.css";


/**
 * @name TransferReviewPage
 * @param {OBJECT} data - Data passed to the page by either a workflow or capture
 * @param {OBJECT} ui - Ui passed to the page by either a workflow or capture
 * @returns {React.Component}
 * @description The main component for the TransferReviewPage
 */
export default function TransferReviewPage(props) {
    // Initalize all model classes
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);
    // Sets the values for the data and ui variables
    const data = { ...getDefaultPageData(), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(data.transfer, globalController), ...props.ui ?? {} };
    const model = useModel();
    const { branding } = useModel();
    const navigate = useRoute();

    const { prevStep, nextStep } = useWorkflow() ?? {};
    // The state to be passed to the state manager
    const state = { data, ui };

    // :: React.state, React.setState -> OBJECT
    // Function to initalize the controller for the state manager
    const controller = (state, setState) => ({
        data: state.data, // Set the data property of the controller
        ui: state.ui, // Set the ui property of the controller

        // prevStep :: Event -> VOID
        // Go to the previous step in the workflow passing an object containing the data
        prevStep: evt => prevStep({ data: props.data }),

        // cancelTransfer :: Event -> VOID
        // Go the previous step passing the default data of the workflow
        cancelTransfer: evt => {
            if(data.editingTransfer.isEditing === "true") {
                navigate("/transferActivity/scheduledTransfers");
            } else {
                prevStep(getDefaultTransfer(globalController, model));
            }
        },

        // completeTransfer :: Event -> VOID
        // Create the transaction and transfer transaction then route to the next step
        // of the workflow passing the data
        completeTransfer: evt => {
            const transfer = state.data.transfer.setDefaults(branding).record();
            const template = state.data.transfer.template;
            let updates = [];

            if(template instanceof TransferTemplate) {
                if(template.needsApproval === true) {
                    transfer.status = "";
                    transfer.needsApproval = true;
                    transfer.levelOfApprover = "0";
                    transfer.numberOfApprovals = "0";
                    transfer.numberOfApprovalsNeeded = "1";
                    transfer.note = template.note;
                }
            }

            if(state.data.editingTransfer.isEditing === "true") {
                transfer.mapID = state.data.editingTransferID.id;
                transfer.id = state.data.editingTransferID.id;
                updates = [Transaction.update(transfer), TransferTransaction.update(transfer)];
            } else {
                transfer.mapID = Transaction.nextID();
                updates = [Transaction.create(transfer), TransferTransaction.create(transfer)];
            }

            BCMSRecord.batch(updates, globalController, Classes);

            if(ui.control.isEditingTransfer) {
                navigate("/transferActivity/scheduledTransfers", { state: { transferUpdated: "true" } });
            } else {
                nextStep({ data: state.data });
            }
        }
    });

    return (
        <PageContainer className="transfer-confirm-page" state={state} controller={controller}>
            <TransferReviewContent />
        </PageContainer>
    );
}

/**
 * @name getDefaultPageData
 * @returns {OBJECT}
 * @description Hook to return the default data required by the page
 */
export const getDefaultPageData = (transferID) => {
    const { branding, user } = GetModel();

    // Note: user settings determine how transfers are made, 
    // so using templates, using only templates, or no templates at all.
    const allowTransferOnlyTemplates = user.allowTransferOnlyTemplates === "true";
    let editTransfer = undefined;
    let editMakeTransfer = undefined;

    if(typeof transferID === "string") {
        editTransfer = TransferTransaction.initByID(transferID);
        editMakeTransfer = MakeTransfer.init(editTransfer, branding)
        editMakeTransfer.templateOnly = allowTransferOnlyTemplates;
    }

    const transfer = editMakeTransfer ? editMakeTransfer : MakeTransfer.init({ templateOnly: allowTransferOnlyTemplates }, branding);

    return {
        transfer: transfer,
        transferTemplates: TransferTemplate.initAll(),
        editingTransfer: { isEditing: transferID ? "true" : "false" },
        editingTransferID: { id: transferID }
    }
};

function GetModel() {
    return useModel();
}

/**
 * @name getDefaultPageUI
 * @param {MakeTransfer} transfer - A MakeTransfer instance used to initalize an instance of MakeTransferUI
 * @return {OBJECT}
 * @description Hook to return the default ui required by the page
 * @note The transfer parameter is not requried, if nothing is passed than an empty instance of MakeTransfer will be used
 */
export const getDefaultPageUI = (transfer, context) => {
    BCMSRecord.globalControllerFor(Classes, context);

    return {
        control: MakeTransferUI.init(transfer ?? MakeTransfer.init(), context.state.branding)
    }
};

/**
 * @name TransferReviewContent
 * @returns {React.Component}
 * @description The component that contains all the content shown for the TransferReviewPage
 */
function TransferReviewContent(props) {
    const { ui, prevStep, cancelTransfer, completeTransfer } = useStateManager();
    const { branding } = useModel();
    const promoText = branding.options.transfers_promo_abi22;

    return (
        <PageContentView>
            <div className="flex-container flex-vertical page-widget-wrapper">
                {promoText && <CustomContentHeaderWidget content={promoText} />}
                <CenterContentContainer>
                    <ContentHeaderView text={ ui.control.isEditingTransfer ? `Review Update` : `Review Transfer` } />

                    <FormContainer className="small-form-width" >
                        <header>Please review the following transfer.</header>
                        <TransferDetails />

                        <FormRowView>
                            <a onClick={prevStep} data-point="Transfers_Edit" className="clickable">Edit Transfer</a>
                        </FormRowView>

                        <FormButtonRow>
                            <UIButton onClick={cancelTransfer} className="secondary-button" text="Cancel" data-point="Transfers_Cancel_Button" />
                            <UIButton className="primary-button" text={ ui.control.isEditingTransfer ? `Update` : `Transfer` } onClick={completeTransfer} data-point="Transfers_Transfer_Button" />
                        </FormButtonRow>
                    </FormContainer>
                </CenterContentContainer>
            </div>
        </PageContentView>
    );
}

/**
 * @name TransferDetails
 * @returns {React.Component}
 * @description Component that lists all the values set in the transfer for the review and confirm page
 */
export function TransferDetails(props) {
    const { data, ui } = useStateManager();

    const transfer = data.transfer;
    const control = ui.control;

    transfer.from = Account.initByType(transfer.from.id);
    transfer.to = Account.initByType(transfer.to.id);

    const balanceLabel = account => {
        let label = ``;
        
        if(Account.isLOC(account)) {
            label = `(${ "Available " + account.availableCredit.format()})`;
        } else if(Account.isUnlinked(account)) {
            label = `(Unlinked Account)`;
        } else {
            label = `(${account.availableBalance ? "Available " + account.availableBalance.format() : "Current " + account.currentBalance.format()})`;
        }

        return label;
    };

    const from = `${transfer.from.nickname} - ${transfer.from.number}${balanceLabel(transfer.from)}`;
    const to = `${transfer.to.nickname} - ${transfer.to.number}${balanceLabel(transfer.to)}`;
    let fromTemplateAccount = ``;
    let toTemplateAccount = ``;

    // Template details will only appear if the user selected a template in the previous page.
    if(control.fromTemplateAccount && control.toTemplateAccount) {
        fromTemplateAccount = `${transfer.fromTemplateAccount.nickname} - ${transfer.fromTemplateAccount.number}${balanceLabel(transfer.fromTemplateAccount)}`;
        toTemplateAccount = `${transfer.toTemplateAccount.nickname} - ${transfer.toTemplateAccount.number}${balanceLabel(transfer.toTemplateAccount)}`;
    }

    const paymentOption = transfer.paymentOption === "Payment Due" && transfer.to.due ? `Payment Due ${transfer.to.due.format()}` :
    transfer.paymentOption === "Entire Balance" && transfer.to.due ? `Entire Balance ${transfer.to.currentBalance.format()}` : transfer.paymentOption;
    const amount = transfer.amount;
    const schedule = transfer.schedule;
    const sendOn = transfer.sendOn;
    const frequency = transfer.frequency;
    const firstTransferDay = transfer.firstTransferDay;
    const secondTransferDay = transfer.secondTransferDay;
    const startDate = transfer.startDate;
    const estimatedDeliveryDate = transfer.estimatedDeliveryDate;
    const duration = transfer.duration;
    const endDate = transfer.endDate;
    const numberOfTransfers = transfer.numberOfTransfers;
    const description = transfer.description;
    const sendEmail = transfer.sendEmail;

    return (
        <>
        {control.fromTemplateAccount &&
                <FormRowView text="From" data-point="Transfers_From">
                    <b>{ fromTemplateAccount }</b>
                </FormRowView>}

            {control.toTemplateAccount &&
                <FormRowView text="To" data-point="Transfers_To">
                    <b>{ toTemplateAccount }</b>
                </FormRowView>}
            {control.from &&
                <FormRowView text="From" data-point="Transfers_From">
                    <b>{from}</b>
                </FormRowView>}

            {control.to &&
                <FormRowView text="To" data-point="Transfers_To">
                    <b>{to}</b>
                </FormRowView>}

            {control.paymentOption &&
                <FormRowView text="Payment Option" data-point="Transfers_Payment_Option">
                    <b>{paymentOption}</b>
                </FormRowView>}

            {control.amount &&
                <FormRowView text="Amount" data-point="Transfers_Amount">
                    <b>{amount.format()}</b>
                </FormRowView>}

            {control.schedule &&
                <FormRowView text="Schedule" data-point="Transfers_Schedule">
                    <b>{schedule}</b>
                </FormRowView>}

            {control.sendOn &&
                <FormRowView text="Send On" data-point="Transfers_Send_On">
                    <b>{sendOn.format("m/d/Y")}</b>
                </FormRowView>}

            {control.frequency &&
                <FormRowView text="Frequency" data-point="Transfers_Frequency">
                    <b>{frequency}</b>
                </FormRowView>}

            {control.firstTransferDay &&
                <FormRowView text="First Transfer Day" data-point="Transfers_First_Day">
                    <b>{firstTransferDay}</b>
                </FormRowView>}

            {control.secondTransferDay &&
                <FormRowView text="Second Transfer Day" data-point="Transfers_Second_Day">
                    <b>{secondTransferDay}</b>
                </FormRowView>}

            {control.startDate &&
                <FormRowView text="Start Date" data-point="Transfers_Start_Date">
                    <b>{startDate.format("m/d/Y")}</b>
                </FormRowView>}

            {control.estimatedDeliveryDate &&
                <FormRowView text="Estimated Delivery Date" data-point="Transfers_Estimated_Delivery">
                    <b>{estimatedDeliveryDate.format("m/d/Y")}</b>
                </FormRowView>}

            {control.duration &&
                <FormRowView text="Duration" data-point="Transfers_Duration">
                    <b>{duration}</b>
                </FormRowView>}

            {control.endDate &&
                <FormRowView text="End Date" data-point="Transfers_End_Date">
                    <b>{endDate.format("m/d/Y")}</b>
                </FormRowView>}

            {control.numberOfTransfers &&
                <FormRowView text="Number of Transfers" data-point="Transfers_Number_Transfers">
                    <b>{numberOfTransfers}</b>
                </FormRowView>}

            {control.description &&
                <FormRowView text="Description" data-point="Transfers_Description">
                    <b>{description}</b>
                </FormRowView>}

            {control.sendEmail &&
                <Branding id="send_email_checkbox_abi22" value="true" default="true">
                    <FormRowView>
                        <UICheckbox value={sendEmail} label="Send me an email when this transfer is completed" disabled={true} data-point="Transfers_Send_Email" />
                    </FormRowView>
                </Branding>}
        </>
    );
}